import React from 'react';
import styled from 'styled-components';
import map from 'lodash/map';

import Button from '@joinforma/ui/components/Button';
import {
  ButtonVariant,
  ButtonSize,
  AutocompleteType,
} from '@joinforma/ui/constants/properties';
import TextField from '@joinforma/ui/components/TextField';
import Select from '@joinforma/ui/components/Select';
import Option from '@joinforma/ui/components/Option';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';

import { UsaState } from 'constants/locations';
import { Form, Field } from 'react-final-form';

import { FormaCard } from './types';
import { Country } from 'constants/countries';

interface Props {
  billing_address: FormaCard['cardHolderDetails']['billingAddress'];
  confirm_button_text: string;
  handleEditBillingAddress: ({
    address,
    card_id,
  }: {
    address: FormaCard['cardHolderDetails']['billingAddress'];
    card_id?: FormaCard['cardId'];
  }) => void;
  onCancel: () => void;
  replace_card_reason?: string;
  card_id?: string;
}

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 40px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  width: 100%;

  @media (max-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    flex-direction: column;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  width: 50%;

  @media (max-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    width: 100%;
    margin: 5px 0;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 66px;
  gap: 10px;
`;

const SubmitButton = styled(Button).attrs({
  variant: ButtonVariant.PRIMARY,
  size: ButtonSize.LARGE,
})``;

const CancelButton = styled(Button).attrs({
  variant: ButtonVariant.SECONDARY,
  size: ButtonSize.LARGE,
})``;

const StyledTextField = styled(TextField)`
  width: 100%;
`;

const StyledSelect = styled(Select)`
  width: 142px;
  margin: 0 15px 0 0;
`;

const EditAddressForm = ({
  handleEditBillingAddress,
  onCancel,
  billing_address,
  confirm_button_text,
  card_id,
}: Props) => {
  const required = (value: boolean | string | number) =>
    value ? undefined : 'This field cannot be blank';

  return (
    <Form
      onSubmit={({ street, street2, city, state, postalCode }) => {
        const address = {
          city,
          street,
          street2,
          postalCode,
          state,
          country: Country.US.toUpperCase(),
        };
        handleEditBillingAddress({ address, card_id });
        onCancel();
      }}
      render={({ handleSubmit }) => (
        <FormContainer onSubmit={handleSubmit}>
          <FormRow>
            <Field
              name="street"
              defaultValue={billing_address.street}
              validate={required}
              render={({ input, meta }) => (
                <InputWrapper>
                  <StyledTextField
                    {...input}
                    label="Mailing Address"
                    autocomplete={AutocompleteType.ADDRESS_LINE1}
                    placeholder="Enter your address"
                    error={meta.touched && meta.error}
                  />
                </InputWrapper>
              )}
            />
            <Field
              name="street2"
              defaultValue={billing_address.street2}
              render={({ input, meta }) => (
                <InputWrapper>
                  <StyledTextField
                    {...input}
                    label="Unit, Apt, or Suite"
                    autocomplete={AutocompleteType.ADDRESS_LINE2}
                    placeholder="Enter your unit"
                    error={meta.touched && meta.error}
                  />
                </InputWrapper>
              )}
            />
          </FormRow>
          <FormRow>
            <Field
              name="city"
              validate={required}
              defaultValue={billing_address.city}
              render={({ input, meta }) => (
                <InputWrapper>
                  <StyledTextField
                    {...input}
                    label="City"
                    autocomplete={AutocompleteType.ADDRESS_LEVEL2}
                    placeholder="Enter City"
                    error={meta.touched && meta.error}
                  />
                </InputWrapper>
              )}
            />
            <InputWrapper>
              <Field
                name="state"
                validate={required}
                defaultValue={billing_address.state}
                render={({ input, meta }) => (
                  <StyledSelect
                    {...input}
                    label="State"
                    autocomplete={AutocompleteType.ADDRESS_LEVEL1}
                    error={meta.touched && meta.error}
                  >
                    {map(UsaState, (v, k) => (
                      <Option key={k} value={k}>
                        {k}
                      </Option>
                    ))}
                  </StyledSelect>
                )}
              />
              <Field
                name="postalCode"
                validate={required}
                defaultValue={billing_address.postalCode}
                render={({ input, meta }) => (
                  <StyledTextField
                    {...input}
                    label="Zip Code"
                    autocomplete={AutocompleteType.POSTAL_CODE}
                    placeholder="Enter Zip Code"
                    error={meta.touched && meta.error}
                  />
                )}
              />
            </InputWrapper>
          </FormRow>
          <ButtonsContainer>
            <CancelButton onClick={onCancel}>Cancel</CancelButton>
            <SubmitButton onClick={handleSubmit}>
              {confirm_button_text}
            </SubmitButton>
          </ButtonsContainer>
        </FormContainer>
      )}
    />
  );
};

export default EditAddressForm;
